import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { 
  Shield, Monitor, Globe, Clock, Download, ExternalLink, Loader2
} from 'lucide-react';
import { API } from '../App';
import { toast } from 'sonner';

const REMOTE_WORK_IMG = "https://images.pexels.com/photos/5244024/pexels-photo-5244024.jpeg";

const Navigation = () => {
  return (
    <nav className="nav-glass fixed top-0 left-0 right-0 z-50" data-testid="vdi-navigation">
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
          <div className="w-10 h-10 bg-[#00F0FF] flex items-center justify-center">
            <Shield className="w-6 h-6 text-black" />
          </div>
          <span className="font-bold text-xl tracking-tight">Digital Associates</span>
        </Link>
        
        <div className="hidden md:flex items-center gap-8">
          <Link to="/search" className="text-[#A1A1AA] hover:text-white transition-colors" data-testid="nav-search">
            AI Search
          </Link>
          <Link to="/services" className="text-[#A1A1AA] hover:text-white transition-colors" data-testid="nav-services">
            Services
          </Link>
          <Link to="/vdi" className="text-white transition-colors" data-testid="nav-vdi">
            Remote Access
          </Link>
          <Link to="/contact" className="text-[#A1A1AA] hover:text-white transition-colors" data-testid="nav-contact">
            Contact
          </Link>
        </div>

        <Link to="/contact">
          <Button className="bg-[#00F0FF] text-black hover:bg-[#00F0FF]/90 btn-glow font-medium">
            Get in Touch
          </Button>
        </Link>
      </div>
    </nav>
  );
};

const ConnectionCard = ({ connection }) => {
  const statusColors = {
    active: 'online',
    expired: 'offline',
    pending: 'pending'
  };

  return (
    <div className="vdi-card p-4 mb-4" data-testid={`connection-${connection.connection_id}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {connection.connection_type === 'vdi' ? (
            <Monitor className="w-5 h-5 text-[#00F0FF]" />
          ) : (
            <Globe className="w-5 h-5 text-[#00FF94]" />
          )}
          <div>
            <p className="font-bold">{connection.connection_type.toUpperCase()}</p>
            <p className="text-xs text-[#A1A1AA] font-mono">{connection.connection_id}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className={`status-dot ${statusColors[connection.status]}`} />
          <span className="text-sm capitalize">{connection.status}</span>
        </div>
      </div>
      {connection.access_url && connection.status === 'active' && (
        <div className="mt-4 pt-4 border-t border-[#27272A]">
          <a 
            href={connection.access_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-[#00F0FF] hover:underline text-sm"
          >
            <ExternalLink className="w-4 h-4" />
            {connection.connection_type === 'vdi' ? 'Launch Virtual Desktop' : 'View VPN Configuration'}
          </a>
        </div>
      )}
    </div>
  );
};

export default function VDIPortal() {
  const navigate = useNavigate();
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedType, setSelectedType] = useState('vdi');
  const [settings, setSettings] = useState(null);
  const [lastConnection, setLastConnection] = useState(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${API}/vdi/settings`);
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
      }
    } catch (error) {
      console.error('Failed to fetch VDI settings:', error);
    }
  };

  const handleRequestAccess = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/vdi/request-access`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          connection_type: selectedType
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to request access');
      }

      const data = await response.json();
      toast.success(`${selectedType.toUpperCase()} access granted!`);
      setLastConnection(data);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="vdi-portal">
      <Navigation />

      {/* Hero */}
      <section 
        className="hero-bg pt-32 pb-16 px-6 relative"
        style={{ backgroundImage: `url(${REMOTE_WORK_IMG})` }}
      >
        <div className="relative z-10 max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-2xl"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Remote <span className="text-[#00FF94]">Access Portal</span>
            </h1>
            <p className="text-xl text-[#A1A1AA]">
              Securely connect to your organization's infrastructure
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Request Access Panel */}
            <div className="lg:col-span-2">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="terminal-card"
              >
                <div className="terminal-header">
                  <div className="terminal-dot red" />
                  <div className="terminal-dot yellow" />
                  <div className="terminal-dot green" />
                  <span className="text-sm text-[#A1A1AA] ml-2 font-mono">remote-access</span>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-6">Request Connection</h3>
                  
                  {/* Connection Type Selection */}
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <button
                      onClick={() => setSelectedType('vdi')}
                      className={`p-4 border transition-colors ${
                        selectedType === 'vdi' 
                          ? 'border-[#00F0FF] bg-[#00F0FF]/10' 
                          : 'border-[#27272A] hover:border-[#00F0FF]/50'
                      }`}
                      data-testid="select-vdi"
                    >
                      <Monitor className={`w-8 h-8 mx-auto mb-2 ${selectedType === 'vdi' ? 'text-[#00F0FF]' : 'text-[#A1A1AA]'}`} />
                      <p className="font-bold">Virtual Desktop</p>
                      <p className="text-xs text-[#A1A1AA]">Full desktop access</p>
                    </button>
                    <button
                      onClick={() => setSelectedType('vpn')}
                      className={`p-4 border transition-colors ${
                        selectedType === 'vpn' 
                          ? 'border-[#00FF94] bg-[#00FF94]/10' 
                          : 'border-[#27272A] hover:border-[#00FF94]/50'
                      }`}
                      data-testid="select-vpn"
                    >
                      <Globe className={`w-8 h-8 mx-auto mb-2 ${selectedType === 'vpn' ? 'text-[#00FF94]' : 'text-[#A1A1AA]'}`} />
                      <p className="font-bold">VPN Connection</p>
                      <p className="text-xs text-[#A1A1AA]">Secure tunnel access</p>
                    </button>
                  </div>

                  {/* Request Button */}
                  <Button
                    onClick={handleRequestAccess}
                    disabled={loading}
                    className={`w-full py-6 ${
                      selectedType === 'vdi' 
                        ? 'bg-[#00F0FF] hover:bg-[#00F0FF]/90' 
                        : 'bg-[#00FF94] hover:bg-[#00FF94]/90'
                    } text-black btn-glow`}
                    data-testid="request-access-btn"
                  >
                    {loading ? (
                      <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    ) : null}
                    Request {selectedType.toUpperCase()} Access
                  </Button>

                  {/* Last Connection Result */}
                  {lastConnection && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mt-6 p-4 bg-[#00FF94]/10 border border-[#00FF94]"
                    >
                      <h4 className="font-bold text-[#00FF94] mb-2">Access Granted!</h4>
                      <p className="text-sm text-[#A1A1AA] mb-3">{lastConnection.instructions}</p>
                      {lastConnection.access_url && (
                        <a 
                          href={lastConnection.access_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 text-[#00F0FF] hover:underline"
                        >
                          <ExternalLink className="w-4 h-4" />
                          {lastConnection.connection_type === 'vdi' ? 'Launch Virtual Desktop' : 'Access VPN Configuration'}
                        </a>
                      )}
                      {lastConnection.vpn_client_download_url && (
                        <a 
                          href={lastConnection.vpn_client_download_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 text-[#00FF94] hover:underline ml-4"
                        >
                          <Download className="w-4 h-4" />
                          Download VPN Client
                        </a>
                      )}
                    </motion.div>
                  )}
                </div>
              </motion.div>

              {/* Instructions */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6"
              >
                <div className="service-card p-6">
                  <Monitor className="w-8 h-8 text-[#00F0FF] mb-4" />
                  <h4 className="font-bold mb-2">VDI Instructions</h4>
                  <p className="text-sm text-[#A1A1AA]">
                    {settings?.vdi_instructions || 'Click the access URL to launch your virtual desktop session. Use your organization credentials to log in.'}
                  </p>
                </div>
                <div className="service-card p-6">
                  <Globe className="w-8 h-8 text-[#00FF94] mb-4" />
                  <h4 className="font-bold mb-2">VPN Instructions</h4>
                  <p className="text-sm text-[#A1A1AA] mb-4">
                    {settings?.vpn_instructions || 'Download and install the VPN client. Import the configuration file and connect using your credentials.'}
                  </p>
                  {settings?.vpn_client_download_url && (
                    <a href={settings.vpn_client_download_url} target="_blank" rel="noopener noreferrer">
                      <Button variant="outline" size="sm" className="border-[#27272A]">
                        <Download className="w-4 h-4 mr-2" />
                        Download VPN Client
                      </Button>
                    </a>
                  )}
                </div>
              </motion.div>
            </div>

            {/* Support Info */}
            <div>
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
              >
                <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-[#A1A1AA]" />
                  Need Help?
                </h3>

                <div className="service-card p-6">
                  <p className="text-[#A1A1AA] mb-4">
                    Having trouble connecting? Our support team is here to help.
                  </p>
                  <div className="space-y-3">
                    <div>
                      <p className="text-xs text-[#A1A1AA] uppercase">Phone</p>
                      <p className="text-[#00F0FF]">0203 582 0582</p>
                    </div>
                    <div>
                      <p className="text-xs text-[#A1A1AA] uppercase">Email</p>
                      <p className="text-[#00F0FF]">support@digital-associates.co.uk</p>
                    </div>
                  </div>
                  <Link to="/contact" className="block mt-4">
                    <Button variant="outline" className="w-full border-[#27272A]">
                      Contact Support
                    </Button>
                  </Link>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-[#27272A]">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center">
          <div className="flex items-center gap-3 mb-4 md:mb-0">
            <div className="w-8 h-8 bg-[#00F0FF] flex items-center justify-center">
              <Shield className="w-4 h-4 text-black" />
            </div>
            <span className="font-bold">Digital Associates</span>
          </div>
          <p className="text-sm text-[#A1A1AA]">
            © 2025 Digital Associates. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
